import { usePrepareCart } from 'hooks/usePrepareCart'
import useQueryString from 'hooks/useQueryString'
import type { TUnavailableProduct } from 'new-components/UnavailableProductModal'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import api from 'services/api'

type TUnavailableApiItem = { product_id: number; name?: string | null }

/**
 * Mensagem do 400 genérico lançado por `assertProductAvailableForOrder` dentro do
 * `generateItems`, quando o produto tem `status = 'I'`.
 */
const UNAVAILABLE_PRODUCT_MESSAGE = /Produto "(.+)" está indisponível/

/**
 * Extrai os itens indisponíveis de um erro do backend.
 *
 * Existem DOIS critérios de indisponibilidade, com respostas diferentes:
 *
 * - **422** com `unavailable_items` — guard de estoque pill
 *   (`origin='pill' AND available_for_sale=false AND stock<=0`). Vale tanto para
 *   `PUT /order_cart(_public)/:id` quanto para `POST /payments/charges`.
 * - **400** com `error: 'Produto "X" está indisponível'` — `status = 'I'` do
 *   produto, lançado dentro do `generateItems`. Não traz `unavailable_items`,
 *   então o `product_id` é resolvido pelo nome nos itens do carrinho.
 */
export const getUnavailableItemsFromError = (error: any, cartItems: any[] = []): TUnavailableApiItem[] => {
  const status = error?.response?.status

  if (status === 422) {
    const items = error?.response?.data?.unavailable_items

    return Array.isArray(items) ? items.filter((item: any) => Number(item?.product_id) > 0) : []
  }

  if (status === 400) {
    const matchedName = String(error?.response?.data?.error || '').match(UNAVAILABLE_PRODUCT_MESSAGE)?.[1]

    if (!matchedName) return []

    const cartItem = cartItems.find(
      (item: any) => (item?.product?.translated_name || item?.product?.name) === matchedName
    )

    const productId = Number(cartItem?.product_id ?? cartItem?.product?.id ?? cartItem?.id)

    // Sem o item no carrinho não há como remover com segurança — deixa o erro seguir.
    if (!(productId > 0)) return []

    return [{ product_id: productId, name: matchedName }]
  }

  return []
}

/**
 * Detecção de indisponibilidade no momento do pagamento (Atividade 2.1/2.2).
 *
 * Quando o backend recusa o carrinho com 422, este hook remove os produtos
 * indisponíveis do pedido e expõe o estado do modal de aviso. O `class_id` é
 * capturado do carrinho local ANTES da remoção, porque o payload do backend
 * traz apenas `product_id` e `name` — e é ele que alimenta o filtro de
 * categoria do botão "Buscar produto".
 */
export const useUnavailableItems = () => {
  const { lastOrderCreated, setOrderData } = usePrepareCart()
  const { filters } = useQueryString()
  const withFilter = filters?.cart_id

  const [unavailableProducts, setUnavailableProducts] = useState<TUnavailableProduct[]>([])
  const [isRemoving, setIsRemoving] = useState(false)

  const closeModal = useCallback(() => setUnavailableProducts([]), [])

  /**
   * Remove os produtos indisponíveis reenviando o carrinho sem eles.
   * Não existe endpoint dedicado de remoção: o `updateOrderCart(Public)`
   * substitui a lista de itens e recalcula frete, cupom e desconto progressivo.
   *
   * Retorna os itens restantes em caso de sucesso, ou `null` se a remoção falhou.
   */
  const removeUnavailableProducts = useCallback(
    async (productIds: number[]) => {
      const orderId = lastOrderCreated?.id

      if (!orderId || productIds.length === 0) return null

      // `value_unitary`/`find_by_ocr` precisam ser reenviados: o `generateItems`
      // do backend recalcula o preço a partir do payload, e omiti-los faz o item
      // com preço negociado pela marca voltar ao preço de tabela.
      const remainingItems = (lastOrderCreated?.items || [])
        .filter((item: any) => !productIds.includes(Number(item?.product_id ?? item?.product?.id ?? item?.id)))
        .map((item: any) => ({
          product_id: item?.product_id || item?.product?.id || item?.id,
          quantity: item?.quantity || 1,
          ...(item?.changed_by_brand ? { value_unitary: item?.value_unitary ?? item?.unitPrice } : {}),
          ...(item?.find_by_ocr ? { find_by_ocr: item.find_by_ocr } : {})
        }))

      const baseUrl = withFilter ? 'order_cart_public' : 'order_cart'

      const { data } = await api.put(`/${baseUrl}/${orderId}`, { items: remainingItems })

      if (data?.order) setOrderData(data.order)

      return remainingItems
    },
    [lastOrderCreated?.id, lastOrderCreated?.items, withFilter, setOrderData]
  )

  /**
   * Trata um erro vindo de qualquer chamada que valide disponibilidade.
   * Retorna `true` quando o erro era de indisponibilidade e já foi tratado
   * (carrinho atualizado + modal aberto), permitindo ao chamador pular o
   * tratamento de erro genérico.
   */
  const handleUnavailableError = useCallback(
    async (error: any) => {
      const cartItems = lastOrderCreated?.items || []
      const unavailableItems = getUnavailableItemsFromError(error, cartItems)

      if (unavailableItems.length === 0) return false

      // Capturado antes da remoção — depois do PUT o item some do carrinho.
      const products: TUnavailableProduct[] = unavailableItems.map((item) => {
        const cartItem = cartItems.find(
          (current: any) =>
            Number(current?.product_id ?? current?.product?.id ?? current?.id) === Number(item.product_id)
        )

        return {
          product_id: Number(item.product_id),
          name: item.name || cartItem?.product?.translated_name || cartItem?.product?.name || null,
          class_id: cartItem?.product?.class_id ?? cartItem?.class_id ?? null
        }
      })

      setUnavailableProducts(products)
      setIsRemoving(true)

      try {
        await removeUnavailableProducts(products.map((product) => product.product_id))
      } catch (removeError) {
        console.error('Erro ao remover produtos indisponíveis do carrinho:', removeError)
        toast.error('Não foi possível atualizar seu carrinho. Recarregue a página e tente novamente.')
      } finally {
        setIsRemoving(false)
      }

      return true
    },
    [lastOrderCreated?.items, removeUnavailableProducts]
  )

  return {
    /** Produtos removidos automaticamente; vazio quando o modal está fechado. */
    unavailableProducts,
    /** Controla a abertura do modal. */
    isUnavailableModalOpen: unavailableProducts.length > 0,
    /** `true` enquanto o carrinho está sendo atualizado no backend. */
    isRemoving,
    handleUnavailableError,
    closeModal
  }
}

export default useUnavailableItems
